import { gql, useQuery } from '@apollo/client'
import { useEffect, useMemo, useState } from 'react'

import {
  FirstComeFirstServedDetailForGuestFragmentDoc,
  FirstComeFirstServedDetailForGuestQuery,
  FirstComeFirstServedDetailForGuestQueryVariables,
  FirstComeFirstServedDetailFragmentDoc,
  FirstComeFirstServedDetailQuery,
  FirstComeFirstServedDetailQueryVariables,
  PaymentDetailFragmentDoc,
  UserDeliveryDetailFragmentDoc,
} from '@/clients/utoniq-core/schema'
import {
  needToRegisterUserAddress,
  needToRegisterUserInfo,
} from '@/components/pages/LotteryDetail/viewModel'
import { config } from '@/config'
import { useCurrentUser, useRouter } from '@/core'
import { useAnalytics } from '@/core/analytics'
import { ConversionCategories } from '@/core/analytics/ConversionKeys'
import { makeViewModel } from '@/core/architecture'
import { App } from '@/core/native'
import {
  getPrefrenceObject,
  removePrefernce,
  setPreference,
} from '@/core/native/storage'
import { useRootAuth } from '@/core/rootAuth'
import { decodeSkuId } from '@/core/sku'
import { createUuid } from '@/lib/uuid'

import { Action, State } from './template'
import { ApplyPreSaleFirstComeFirstServedValue } from './template/FirstComeFirstServedDetailForm'

/**
 *
 */
export const useViewModel = makeViewModel<State, Action>(() => {
  const { user } = useCurrentUser()
  const { analytics } = useAnalytics()
  const { router, generatePath, routerPages } = useRouter<{
    preSaleId: string
    itemId: string
    quantity: string
    skuId: string
  }>()
  const [choice, setChoice] = useState<State['choices']>()
  const rootAuthInfo = useRootAuth()
  const { data, loading } = useQuery<
    FirstComeFirstServedDetailQuery,
    FirstComeFirstServedDetailQueryVariables
  >(QUERY, {
    variables: {
      id: router.query.preSaleId,
      clientId: rootAuthInfo.clientId,
      token: rootAuthInfo.token,
    },
    skip: !user?.isSigned,
    onCompleted({ preSale }) {
      if (preSale) {
        analytics?.track('view_item_list', {
          item_list_id: `preSale_list_${preSale.id}`,
          item_list_name: 'PreSale Items',
          items: preSale.items.map(item => ({
            item_id: item.id,
            item_name: item.title,
            item_category: ConversionCategories.preSaleItem,
            item_category2: item.type,
            item_category3: App.getPlatform(),
            item_category4: 1,
            item_category5: preSale.id,
            price: item.price,
          })),
        })
      }
    },
  })

  const { data: dataForGuest, loading: loadingForGuest } = useQuery<
    FirstComeFirstServedDetailForGuestQuery,
    FirstComeFirstServedDetailForGuestQueryVariables
  >(QUERY_FOR_GUEST, {
    variables: {
      id: router.query.preSaleId,
      clientId: rootAuthInfo.clientId,
      token: rootAuthInfo.token,
    },
    skip: !!user?.isSigned,
  })

  useEffect(() => {
    ;(async () => {
      // 画面ロード時に冪等キーをリフレッシュする
      await setPreference('idempotencyKey', createUuid())

      const v = await getPrefrenceObject<ApplyPreSaleFirstComeFirstServedValue>(
        'preSaleLotteryApplicationInput'
      )

      // 戻るボタンによる遷移の場合、購入内容を保持する
      if (v.preSaleId === router.query.preSaleId) {
        setChoice(
          v.choices.map((c, i) => ({
            itemId: c.itemId,
            skuId: c.skuId,
            count: v.purchaseCountChoices[i].count,
          }))[0]
        )
      }
      // LocalStorageに保存したFormの値保持用の一次データを削除
      await removePrefernce('preSaleLotteryApplicationInput')
    })()
  }, [])

  const fees = (data?.preSale ?? dataForGuest?.preSale)?.items?.[0]?.fees
  const paymentFees = useMemo(
    () =>
      (fees?.collections || [])
        .concat(fees?.konbini ? [fees.konbini] : [])
        .concat(fees?.shipping ? [fees.shipping] : [])
        .filter(fee => !!fee)
        .map(fee => ({
          feeName: fee?.feeName,
          fee: fee?.fee ?? 0,
          per: fee?.per,
          feeType: fee?.feeType,
          formattedFee: fee?.formattedFee,
        })),
    [fees]
  )

  return {
    state: {
      user,
      preSaleId: router.query.preSaleId,
      loading: loading || loadingForGuest,
      preSale: data?.preSale ?? dataForGuest?.preSale,
      paymentFees,
      originFullNameValue: data?.user?.fullName ?? '',
      paymentSource: data?.paymentSource,
      choices:
        choice ??
        (router.query.itemId
          ? {
              itemId: router.query.itemId,
              skuId: router.query.skuId && decodeSkuId(router.query.skuId),
              count: Number(router.query.quantity) ?? 1,
            }
          : undefined),
    },
    actions: {
      async onReadyPaymentMethod(
        values?: ApplyPreSaleFirstComeFirstServedValue
      ) {
        if (values?.choices.length) {
          // 内部ストレージに保存し、遷移先で取り出す
          await setPreference('preSaleLotteryApplicationInput', values)
        }
        router.push(routerPages.PaymentMethodSetting)
      },
      async onSubmit(
        values: ApplyPreSaleFirstComeFirstServedValue | undefined
      ) {
        if (!values) return

        // 内部ストレージに保存し、遷移先で取り出す
        await setPreference('preSaleLotteryApplicationInput', values)
        const item = data?.preSale.items.find(
          item => item.id === values?.choices?.[0]?.itemId
        )

        await analytics?.track('click_preSaleFcfsConfirm', {
          applicationKey: config.applicationKey,
          preSaleId: router.query.preSaleId,
          paymentMethod: values?.paymentMethod,
        })

        await analytics?.track('add_to_cart', {
          item_list_id: `preSale_list_${router.query.preSaleId}`,
          item_list_name: 'PreSale Items',
          isInstant: true,
          isAnonymous: false,
          items: [
            {
              item_id: item?.id,
              item_name: item?.title,
              item_category: ConversionCategories.preSaleItem,
              item_category2: item?.type,
              item_category3: App.getPlatform(),
              item_category4: values?.choices?.[0]?.skuId,
              item_category5: router.query.preSaleId,
              price: item?.price,
              quantity: values.purchaseCountChoices?.[0]?.count,
            },
          ],
        })

        if (
          needToRegisterUserInfo(
            data?.preSale.firstComeFirstServed
              ?.personalInformationRequirements ?? [],
            data?.user
          )
        ) {
          return router.push(
            generatePath(routerPages.LotteryUserInfo, {
              preSaleId: router.query.preSaleId,
            })
          )
        }

        if (
          needToRegisterUserAddress(
            data?.preSale.firstComeFirstServed
              ?.personalInformationRequirements ?? [],
            data?.user
          )
        ) {
          return router.push(
            generatePath(routerPages.LotteryDeliveryInfo, {
              preSaleId: router.query.preSaleId,
            })
          )
        }

        router.push(
          generatePath(routerPages.FirstComeFirstServedConfirmation, {
            preSaleId: router.query.preSaleId,
          })
        )
      },
    },
  }
})

const QUERY = gql`
  query FirstComeFirstServedDetail(
    $id: ID!
    $clientId: String
    $token: String
  ) {
    preSale(id: $id, clientId: $clientId, token: $token) {
      ...FirstComeFirstServedDetail
    }
    paymentSource {
      ...PaymentDetail
    }
    user {
      ...UserDeliveryDetail
    }
  }
  ${FirstComeFirstServedDetailFragmentDoc}
  ${PaymentDetailFragmentDoc}
  ${UserDeliveryDetailFragmentDoc}
`

const QUERY_FOR_GUEST = gql`
  query FirstComeFirstServedDetailForGuest(
    $id: ID!
    $clientId: String
    $token: String
  ) {
    preSale(id: $id, clientId: $clientId, token: $token) {
      ...FirstComeFirstServedDetailForGuest
    }
  }
  ${FirstComeFirstServedDetailForGuestFragmentDoc}
`
